import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, X, Check, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

export default function RazorpayModal({ 
  isOpen, 
  onClose, 
  amountInINR, 
  description, 
  clientName, 
  clientEmail, 
  clientPhone, 
  onSuccess 
}) {
  // 'idle' | 'loading' | 'paying' | 'verifying' | 'success' | 'failed'
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const rzpRef = useRef(null);

  // Helper to dynamically load script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const initiatePayment = async () => {
    try {
      setStatus('loading');
      setErrorMsg('');

      // 1. Load Razorpay SDK
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setStatus('failed');
        setErrorMsg('Failed to load payment gateway. Please check your internet connection.');
        return;
      }

      // 2. Create Order on backend
      const res = await api.post('/api/saas/clients/payment/create-order', {
        amount: amountInINR
      });

      if (!res.data.success) {
        setStatus('failed');
        setErrorMsg(res.data.message || 'Failed to initialize order.');
        return;
      }

      const { orderId, keyId } = res.data;

      // 3. Configure Razorpay Options
      const options = {
        key: keyId,
        amount: Math.round(amountInINR * 100),
        currency: 'INR',
        name: 'Digital Repair Shop System',
        description: description || 'SaaS Plan Subscription',
        order_id: orderId,
        handler: async function (response) {
          try {
            setStatus('verifying');
            // 4. Verify payment on backend
            const verifyRes = await api.post('/api/saas/clients/payment/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyRes.data.success) {
              setStatus('success');
              toast.success('Payment completed successfully!');
              setTimeout(() => {
                onSuccess(response.razorpay_payment_id);
              }, 1500);
            } else {
              setStatus('failed');
              setErrorMsg('Payment verification failed.');
            }
          } catch (err) {
            console.error('Verification error:', err);
            setStatus('failed');
            setErrorMsg(err.response?.data?.message || 'Server error verifying payment.');
          }
        },
        prefill: {
          name: clientName || '',
          email: clientEmail || '',
          contact: clientPhone || ''
        },
        theme: {
          color: '#4f46e5'
        },
        modal: {
          ondismiss: function () {
            setStatus('failed');
            setErrorMsg('Payment cancelled by user.');
          }
        }
      };

      setStatus('paying');
      rzpRef.current = new window.Razorpay(options);
      rzpRef.current.open();

    } catch (err) {
      console.error('Initiate payment error:', err);
      setStatus('failed');
      setErrorMsg(err.response?.data?.message || 'Failed to start secure payment.');
    }
  };

  useEffect(() => {
    if (isOpen) {
      initiatePayment();
    } else {
      setStatus('idle');
      setErrorMsg('');
      if (rzpRef.current) {
        rzpRef.current = null;
      }
    }
  }, [isOpen, amountInINR]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center z-[9999] p-4 font-sans">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[400px] overflow-hidden border border-slate-100 flex flex-col min-h-[300px] justify-between p-6 relative animate-fadeIn">
        
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-indigo-600" />
            <h3 className="text-base font-extrabold text-slate-900">Secure Checkout</h3>
          </div>
          <button 
            onClick={onClose} 
            className="text-slate-400 hover:text-slate-600 bg-slate-100 hover:bg-slate-200 p-1.5 rounded-full transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body Content */}
        <div className="flex-1 flex flex-col items-center justify-center py-6 text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
              <div>
                <p className="font-extrabold text-slate-800">Initializing Payment</p>
                <p className="text-xs text-slate-500 mt-1">Contacting secure servers, please wait...</p>
              </div>
            </>
          )}

          {status === 'paying' && (
            <>
              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
              <div>
                <p className="font-extrabold text-slate-800">Waiting for Gateway</p>
                <p className="text-xs text-slate-500 mt-1">Complete payment in the secure Razorpay popup window.</p>
              </div>
            </>
          )}

          {status === 'verifying' && (
            <>
              <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
              <div>
                <p className="font-extrabold text-slate-800 text-emerald-600">Verifying Signature</p>
                <p className="text-xs text-slate-500 mt-1">Securing connection & updating subscription...</p>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-12 h-12 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center text-emerald-650 shadow-md">
                <Check className="w-6 h-6 stroke-[3]" />
              </div>
              <div>
                <p className="font-extrabold text-slate-800 text-lg">Payment Successful</p>
                <p className="text-xs text-emerald-600 font-semibold mt-1">Your account is active. Redirecting...</p>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-12 h-12 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center text-red-500">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="font-extrabold text-slate-800">Payment Failed</p>
                <p className="text-xs text-red-500 mt-1 font-semibold max-w-[280px] leading-relaxed">
                  {errorMsg || 'Could not process transaction.'}
                </p>
              </div>
              <button
                onClick={initiatePayment}
                className="mt-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-6 py-2 rounded-xl text-xs transition-all active:scale-95 shadow-sm"
              >
                Retry Payment
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="pt-4 border-t border-slate-100 flex items-center justify-center gap-1.5 text-[9px] text-slate-400 font-bold uppercase tracking-wider">
          <ShieldCheck className="w-4 h-4 text-emerald-600" /> Powered by Razorpay Secure Gateway
        </div>

      </div>
    </div>
  );
}
